/**
 * Vue maire — niveau 2 (détail par service).
 *
 * Page générique pilotée par l'API : KPIs bento, courbe de tendance (barres sur
 * 12 semaines), répartition (top catégories), carte des points, et bloc de
 * « décisions rapides » actionnables. L'action de chaque décision est résolue
 * localement vers l'endpoint métier EXISTANT du module concerné — la vue maire
 * ne réinvente aucune logique.
 *
 * Design RORONOA : dark, glassmorphism, accent par module (tokens OKLCH).
 */

import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AppHeader } from '@/components/AppHeader';
import { ActiviteMensuelleCard } from '@/components/dashboard/ActiviteMensuelleCard';
import { Card } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { useConfirmDialog } from '@/lib/use-confirm-dialog';
import { api } from '@/lib/api';
import type { DashboardDetail, DetailKpi, QuickDecision, KpiDetail } from '@/lib/api';
import { ArrowLeft, ArrowRight, Check, AlertTriangle, MapPin, Info } from 'lucide-react';

// Accent CSS par module (cohérent avec la vue d'ensemble)
const MODULE_ACCENT: Record<string, string> = {
  tech: 'tech',
  grc: 'grc',
  ecole: 'cantine',
  asso: 'asso',
  salle: 'tech',
  rh: 'rh',
  compta: 'compta',
  petite_enfance: 'petite_enfance',
  urba: 'urba',
};

function kpiToneStyle(tone?: string): { bg: string; border: string } {
  switch (tone) {
    case 'danger':
      return {
        bg: 'linear-gradient(180deg, oklch(0.30 0.14 25 / 0.20), oklch(var(--c-bg) / 0.5))',
        border: 'oklch(0.68 0.22 25 / 0.30)',
      };
    case 'good':
      return {
        bg: 'linear-gradient(180deg, oklch(0.30 0.12 155 / 0.18), oklch(var(--c-bg) / 0.5))',
        border: 'oklch(0.72 0.18 155 / 0.28)',
      };
    case 'accent':
      return {
        bg: 'linear-gradient(180deg, oklch(var(--c-tech) / 0.12), oklch(var(--c-bg) / 0.5))',
        border: 'oklch(var(--c-tech) / 0.25)',
      };
    default:
      return {
        bg: 'linear-gradient(180deg, oklch(var(--c-surface) / 0.55), oklch(var(--c-bg) / 0.5))',
        border: 'oklch(var(--c-border))',
      };
  }
}

function decTone(tone: string): { bg: string; border: string; fg: string } {
  switch (tone) {
    case 'danger':
      return {
        bg: 'linear-gradient(180deg, oklch(0.30 0.14 25 / 0.18), oklch(var(--c-bg) / 0.55))',
        border: 'oklch(0.68 0.22 25 / 0.32)',
        fg: 'oklch(0.90 0.10 25)',
      };
    case 'warn':
      return {
        bg: 'linear-gradient(180deg, oklch(0.30 0.12 75 / 0.16), oklch(var(--c-bg) / 0.55))',
        border: 'oklch(0.80 0.17 75 / 0.30)',
        fg: 'oklch(0.90 0.09 75)',
      };
    default:
      return {
        bg: 'linear-gradient(180deg, oklch(var(--c-tech) / 0.14), oklch(var(--c-bg) / 0.55))',
        border: 'oklch(var(--c-tech) / 0.30)',
        fg: 'oklch(var(--c-tech-300))',
      };
  }
}

export default function VueMaireDetail() {
  const { module = '' } = useParams<{ module: string }>();
  const navigate = useNavigate();
  const { confirm, dialogElement } = useConfirmDialog();

  const [data, setData] = useState<DashboardDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Modale de ventilation d'un KPI
  const [kpiModal, setKpiModal] = useState<{ label: string; detail: KpiDetail | null; loading: boolean } | null>(null);

  // Ouvre la ventilation d'un KPI cliquable : modale interne (jamais de lien
  // externe, pour rester compatible APK) ou navigation vers l'écran du module.
  const onKpiClick = useCallback(
    async (kpi: DetailKpi) => {
      if (!kpi.drill) return;
      if (kpi.drill.type === 'navigate') {
        navigate(kpi.drill.to);
        return;
      }
      const key = kpi.drill.key;
      setKpiModal({ label: kpi.label, detail: null, loading: true });
      try {
        const detail = await api.dashboard.kpiDetail(module, key);
        setKpiModal({ label: kpi.label, detail, loading: false });
      } catch {
        setKpiModal({ label: kpi.label, detail: { title: kpi.label, rows: [], note: 'Détail indisponible.' }, loading: false });
      }
    },
    [module, navigate]
  );

  const accent = MODULE_ACCENT[module] ?? 'tech';

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await api.dashboard.detail(module);
      setData(r);
    } catch {
      setError('Cette vue détail n\'est pas disponible.');
    } finally {
      setLoading(false);
    }
  }, [module]);

  useEffect(() => {
    void load();
  }, [load]);

  // Résout l'action d'une décision rapide vers l'endpoint métier existant.
  // Actions simples sans paramètre → exécutées en un clic (avec confirmation).
  // Actions à contexte (montant, décision, formulaire) → navigation vers le module.
  const runDecision = useCallback(
    async (d: QuickDecision) => {
      try {
        switch (d.kind) {
          // ── Service technique ────────────────────────────────────────────
          case 'signalement_urgent': {
            const ok = await confirm({
              title: 'Prioriser ce signalement ?',
              description: `« ${d.title} » sera marqué planifié pour traitement prioritaire.`,
              confirmLabel: 'Prioriser',
            });
            if (!ok) return;
            setBusyId(d.id);
            await api.serviceTechnique.signalements.updateStatut(d.id, { statut: 'planifie' });
            await load();
            break;
          }
          case 'tournee':
            navigate('/service-technique/planning');
            break;

          // ── GRC ──────────────────────────────────────────────────────────
          case 'alerte_diffuser': {
            const ok = await confirm({
              title: 'Diffuser cette alerte municipale ?',
              description: `« ${d.title} » sera diffusée sur les canaux configurés.`,
              confirmLabel: 'Diffuser',
            });
            if (!ok) return;
            setBusyId(d.id);
            await api.grc.alertes.trigger(d.id);
            await load();
            break;
          }
          case 'alerte_resoudre': {
            const ok = await confirm({
              title: 'Clôturer cette alerte ?',
              description: `« ${d.title} » sera marquée résolue.`,
              confirmLabel: 'Clôturer',
            });
            if (!ok) return;
            setBusyId(d.id);
            await api.grc.alertes.resolve(d.id);
            await load();
            break;
          }

          // ── Scolaire / cantine ───────────────────────────────────────────
          case 'facturation':
            navigate('/scolaire/facturation');
            break;
          case 'dossier_scolaire':
            navigate('/scolaire/familles');
            break;

          // ── Associations ─────────────────────────────────────────────────
          case 'subvention':
            navigate('/associations');
            break;

          // ── Salles & matériel ────────────────────────────────────────────
          case 'caution':
          case 'reservation_salle':
            navigate('/materiel/reservations');
            break;

          // ── RH ───────────────────────────────────────────────────────────
          case 'rh_absence':
          case 'rh_heures_sup':
            navigate('/rh');
            break;

          default:
            break;
        }
      } catch {
        setError('L\'action n\'a pas pu être effectuée. Réessayez.');
      } finally {
        setBusyId(null);
      }
    },
    [confirm, load, navigate]
  );

  // Libellé du bouton principal selon le type de décision.
  const decisionLabel = (kind: string): string => {
    switch (kind) {
      case 'signalement_urgent':
        return 'Prioriser';
      case 'alerte_diffuser':
        return 'Diffuser';
      case 'alerte_resoudre':
        return 'Clôturer';
      case 'facturation':
        return 'Lancer';
      case 'subvention':
      case 'dossier_scolaire':
        return 'Décider';
      case 'caution':
        return 'Traiter';
      default:
        return 'Valider';
    }
  };

  const maxTrend = data ? Math.max(1, ...data.trend.points.map((p) => p.value)) : 1;

  return (
    <div className="min-h-screen text-ink">
      <AppHeader module="Pilotage" accent={accent as 'tech'} />

      <main className="mx-auto max-w-5xl px-4 py-5 sm:px-6">
        {/* Barre de retour */}
        <div className="mb-4 flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/dashboard/maire')}
            className="flex h-9 w-9 items-center justify-center rounded-r-md border border-border bg-surface/60 text-ink-muted transition-colors hover:bg-surface"
            aria-label="Retour à la vue d'ensemble"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          </button>
          <div>
            <div className="text-xs uppercase tracking-wide text-ink-subtle">Vue maire · détail service</div>
            <h1 className="font-display text-lg font-semibold tracking-r-snug text-ink">
              {data?.label ?? 'Détail'}
            </h1>
          </div>
        </div>

        {error && (
          <div className="mb-4 rounded-r-md border border-urgent/40 bg-urgent/10 px-4 py-3 text-sm text-ink">
            {error}
          </div>
        )}

        {loading && (
          <div className="flex min-h-[40vh] items-center justify-center">
            <div
              className="h-10 w-10 animate-spin rounded-full border-2 border-t-transparent"
              style={{ borderColor: `oklch(var(--c-${accent}))`, borderTopColor: 'transparent' }}
              role="status"
              aria-label="Chargement"
            />
          </div>
        )}

        {!loading && data && (
          <>
            {/* KPIs bento */}
            <div className="mb-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {data.kpis.map((k: DetailKpi, i) => {
                const s = kpiToneStyle(k.tone);
                const clickable = !!k.drill;
                return (
                  <button
                    key={i}
                    type="button"
                    onClick={() => clickable && void onKpiClick(k)}
                    disabled={!clickable}
                    className={`rounded-r-md border p-3 text-left backdrop-blur-glass transition ${
                      clickable ? 'cursor-pointer hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-white/20' : 'cursor-default'
                    }`}
                    style={{ background: s.bg, borderColor: s.border }}
                  >
                    <div className="flex items-center gap-1 text-xs text-ink-subtle">
                      {k.label}
                      {clickable && <Info className="h-3 w-3 opacity-50" aria-hidden="true" />}
                    </div>
                    <div className="mt-1 text-2xl font-semibold tabular-nums text-ink">{k.value}</div>
                    {k.sub && <div className="mt-0.5 text-[11px] text-ink-subtle">{k.sub}</div>}
                  </button>
                );
              })}
            </div>

            <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
              {/* Tendance (barres) */}
              <Card variant="glass" className="p-4">
                <div className="mb-3 flex items-baseline gap-2">
                  <div className="font-display text-sm font-semibold text-ink">{data.trend.title}</div>
                  <div className="ml-auto text-[11px] text-ink-subtle">{data.trend.unit}</div>
                </div>
                <div className="flex h-28 items-end gap-1">
                  {data.trend.points.map((p, i) => (
                    <div
                      key={i}
                      className="flex-1 rounded-t"
                      title={`${p.label} : ${p.value}`}
                      style={{
                        height: `${Math.max(4, (p.value / maxTrend) * 100)}%`,
                        background: p.highlight
                          ? `linear-gradient(180deg, oklch(var(--c-${accent})), oklch(var(--c-${accent}) / 0.5))`
                          : `linear-gradient(180deg, oklch(var(--c-${accent}) / 0.5), oklch(var(--c-${accent}) / 0.25))`,
                        borderTop: `1px solid oklch(var(--c-${accent}) / 0.5)`,
                      }}
                    />
                  ))}
                </div>
                <div className="mt-1.5 flex justify-between text-[10px] text-ink-subtle">
                  {data.trend.points
                    .filter((_, i) => i % 3 === 0 || i === data.trend.points.length - 1)
                    .map((p, i) => (
                      <span key={i}>{p.label}</span>
                    ))}
                </div>
              </Card>

              {/* Répartition / top catégories */}
              {data.breakdown && (
                <Card variant="glass" className="p-4">
                  <div className="mb-3 flex items-baseline gap-2">
                    <div className="font-display text-sm font-semibold text-ink">{data.breakdown.title}</div>
                    {data.breakdown.meta && (
                      <div className="ml-auto text-[11px] text-ink-subtle">{data.breakdown.meta}</div>
                    )}
                  </div>
                  {data.breakdown.items.length === 0 ? (
                    <div className="py-6 text-center text-sm text-ink-subtle">Aucune donnée</div>
                  ) : (
                    <div className="space-y-2.5">
                      {data.breakdown.items.map((it, i) => (
                        <div key={i}>
                          <div className="mb-1 flex justify-between text-xs">
                            <span className="text-ink-muted">{it.label}</span>
                            <span className="font-semibold text-ink">{it.value}</span>
                          </div>
                          <div className="h-1.5 overflow-hidden rounded-full bg-surface/60">
                            <div
                              className="h-full rounded-full"
                              style={{
                                width: `${it.pct}%`,
                                background: it.color ?? `oklch(var(--c-${accent}))`,
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </Card>
              )}
            </div>

            {/* M11 (retour Manon) — activité mensuelle rapportée à l'effectif. */}
            {data.activiteMensuelle && data.activiteMensuelle.mois.length > 0 && (
              <ActiviteMensuelleCard activite={data.activiteMensuelle} accent={accent} />
            )}

            {/* Carte des points (si fournie) */}
            {data.map && data.map.points.length > 0 && (
              <Card variant="glass" className="mt-3 p-4">
                <div className="mb-2 flex items-center gap-2">
                  <MapPin className="h-4 w-4 text-ink-muted" aria-hidden="true" />
                  <div className="font-display text-sm font-semibold text-ink">{data.map.title}</div>
                  <div className="ml-auto text-[11px] text-ink-subtle">{data.map.points.length} point(s)</div>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {data.map.points.map((pt, i) => (
                    <span
                      key={i}
                      className="h-2.5 w-2.5 rounded-full"
                      title={`${pt.lat.toFixed(4)}, ${pt.lng.toFixed(4)}`}
                      style={{
                        background:
                          pt.tone === 'danger'
                            ? 'oklch(0.68 0.22 25)'
                            : pt.tone === 'good'
                              ? 'oklch(0.72 0.18 155)'
                              : 'oklch(0.80 0.17 75)',
                      }}
                    />
                  ))}
                </div>
                <p className="mt-2 text-[11px] text-ink-subtle">
                  Vue agrégée des positions · la carte interactive complète est dans le module.
                </p>
              </Card>
            )}

            {/* Décisions rapides */}
            <div className="mb-2 mt-5 text-xs uppercase tracking-wide text-ink-subtle">
              Décisions rapides · le maire peut agir
            </div>
            {data.decisions.length === 0 ? (
              <Card variant="glass" className="p-5">
                <div className="text-center text-sm text-ink-muted">
                  Aucune décision en attente — tout est à jour.
                </div>
                {data.decisionTypes && data.decisionTypes.length > 0 && (
                  <div className="mt-4 border-t border-border pt-4">
                    <div className="mb-2 text-[11px] uppercase tracking-wide text-ink-subtle">
                      Ce qui peut remonter ici
                    </div>
                    <ul className="space-y-1.5">
                      {data.decisionTypes.map((t, i) => (
                        <li key={i} className="flex items-center gap-2 text-xs text-ink-muted">
                          <span
                            className="h-1.5 w-1.5 flex-shrink-0 rounded-full"
                            style={{ background: `oklch(var(--c-${accent}))` }}
                          />
                          {t}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </Card>
            ) : (
              <div className="space-y-2">
                {data.decisions.map((d) => {
                  const t = decTone(d.tone);
                  return (
                    <div
                      key={`${d.kind}-${d.id}`}
                      className="flex items-center gap-3 rounded-r-md border p-3 backdrop-blur-glass"
                      style={{ background: t.bg, borderColor: t.border }}
                    >
                      <div className="min-w-0 flex-1">
                        <div
                          className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wide"
                          style={{ color: t.fg }}
                        >
                          {d.tone === 'danger' && <AlertTriangle className="h-3 w-3" aria-hidden="true" />}
                          {d.tag}
                        </div>
                        <div className="mt-0.5 truncate text-sm text-ink">{d.title}</div>
                        {d.meta && <div className="text-[11px] text-ink-subtle">{d.meta}</div>}
                      </div>
                      <div className="flex flex-shrink-0 items-center gap-2">
                        <button
                          type="button"
                          onClick={() => void runDecision(d)}
                          disabled={busyId === d.id}
                          className="inline-flex items-center gap-1.5 rounded-r-md px-3 py-1.5 text-sm font-medium text-white disabled:opacity-60"
                          style={{
                            background: `linear-gradient(135deg, oklch(var(--c-${accent})), oklch(var(--c-${accent}) / 0.7))`,
                          }}
                        >
                          {busyId === d.id ? (
                            '…'
                          ) : (
                            <>
                              {d.kind === 'signalement_urgent' ||
                              d.kind === 'alerte_diffuser' ||
                              d.kind === 'alerte_resoudre' ? (
                                <Check className="h-3.5 w-3.5" aria-hidden="true" />
                              ) : null}
                              {decisionLabel(d.kind)}
                              {d.kind !== 'signalement_urgent' &&
                              d.kind !== 'alerte_diffuser' &&
                              d.kind !== 'alerte_resoudre' ? (
                                <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />
                              ) : null}
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <p className="mt-5 text-center text-xs text-ink-subtle">Données temps réel · {data.label}</p>
          </>
        )}
      </main>
      {/* Modale de ventilation d'un KPI */}
      <Dialog
        open={kpiModal !== null}
        onClose={() => setKpiModal(null)}
        title={kpiModal?.detail?.title ?? kpiModal?.label ?? 'Détail'}
        size="lg"
        closeOnBackdropClick
      >
        {kpiModal?.loading ? (
          <div className="py-8 text-center text-sm text-ink-subtle">Chargement…</div>
        ) : kpiModal?.detail ? (
          <div>
            {kpiModal.detail.total && (
              <div className="mb-3 flex items-baseline justify-between border-b border-border pb-2">
                <span className="text-sm text-ink-muted">Total</span>
                <span className="text-lg font-semibold tabular-nums text-ink">{kpiModal.detail.total}</span>
              </div>
            )}
            {kpiModal.detail.rows.length === 0 ? (
              <div className="py-6 text-center text-sm text-ink-subtle">Aucune ligne à afficher.</div>
            ) : (
              <ul className="max-h-80 space-y-1 overflow-y-auto">
                {kpiModal.detail.rows.map((r, i) => (
                  <li
                    key={i}
                    className="flex items-center justify-between gap-3 rounded-r-sm border border-border/60 bg-surface/40 px-3 py-2"
                  >
                    <div className="min-w-0">
                      <div className="truncate text-sm text-ink">{r.label}</div>
                      {r.meta && <div className="text-[11px] text-ink-subtle">{r.meta}</div>}
                    </div>
                    <div className="flex-shrink-0 text-sm font-semibold tabular-nums text-ink">{r.value}</div>
                  </li>
                ))}
              </ul>
            )}
            {kpiModal.detail.note && (
              <p className="mt-3 flex items-start gap-1.5 text-[11px] leading-relaxed text-ink-subtle">
                <Info className="mt-0.5 h-3 w-3 flex-shrink-0" aria-hidden="true" />
                {kpiModal.detail.note}
              </p>
            )}
          </div>
        ) : null}
      </Dialog>

      {dialogElement}
    </div>
  );
}
